/**
 * useLastRoute — persist + restore the current route across Android
 * Activity recreation (process death).
 *
 * Problem:
 *   On Android, when the OS kills the app's WebView process (memory
 *   pressure, background, low-RAM device), and the user returns, the
 *   WebView cold-boots from https://localhost/.  DashboardRouter sees "/"
 *   and redirects every user to /overview — even if they were on /reports
 *   or /alarms/:id.
 *
 *   iOS WKWebView freezes-in-memory on background, so this never happens
 *   there.
 *
 * Solution:
 *   - usePersistRoute(): call inside AppShell.  Listens to every
 *     React Router location change and writes the path to localStorage.
 *   - readLastRoute(): synchronous read called by DashboardRouter before
 *     its first render.  Returns the saved path, or null if none / invalid.
 *
 * Storage: localStorage (synchronous, no async flash).  Cleared on
 * sign-out so an expired session never restores a stale deep-link.
 */

import * as React from "react";
import { useLocation } from "react-router";

const KEY = "mv2:last-route";

/** Paths that should never be restored (auth flows, root redirector). */
const SKIP = ["/", "/login", "/forgot-password", "/accept-invite"];

function isSafe(path: string): boolean {
  return !SKIP.some((s) => path === s || path.startsWith(s + "?"));
}

/**
 * Mount inside AppShell (inside RouterProvider) to persist the current
 * path on every navigation.
 */
export function usePersistRoute(): void {
  const location = useLocation();
  React.useEffect(() => {
    if (isSafe(location.pathname)) {
      try {
        localStorage.setItem(KEY, location.pathname + location.search);
      } catch {
        // Storage quota or private-mode restriction — no-op.
      }
    }
  }, [location.pathname, location.search]);
}

/**
 * Read the last persisted route synchronously.  Returns null if nothing
 * was saved, if the path is on the skip-list, or if the path looks
 * structurally invalid.
 */
export function readLastRoute(): string | null {
  try {
    const saved = localStorage.getItem(KEY);
    if (!saved || !isSafe(saved)) return null;
    // Basic sanity — must start with /
    if (!saved.startsWith("/")) return null;
    return saved;
  } catch {
    return null;
  }
}

/**
 * Clear the saved route on sign-out so a fresh login always lands on the
 * persona-default home rather than a stale deep-link.
 */
export function clearLastRoute(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // no-op
  }
}
